import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  AdDetails,
  GetAdsResponse,
  GetPopularRequestsResponse,
} from '@/api/types/ads.types';
import {
  CategoryBaseInfo,
  GetCategoryPropertiesResponse,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { GetRegionsResponse } from '@/api/types/regions.types';

interface ServerDataContextProps {
  initialAdDetails: AdDetails | null;
  initialPremiumAds: GetAdsResponse | null;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
  currentCategory: CategoryBaseInfo | null;
  initialVipAds: GetAdsResponse | null;
  initialLatestAds: GetAdsResponse | null;
  initialUserAds: GetAdsResponse | null;
  categoryProperties: GetCategoryPropertiesResponse | null;
  regions: GetRegionsResponse | null;
  h1: string | null;
}

const ServerDataContext = createContext<ServerDataContextProps | undefined>(
  undefined
);

export const ServerDataProvider = ({
  children,
  adDetails,
  premiumAds,
  popularRequests,
  regions,
  rootCategories,
  currentCategory,
  vipAds,
  latestAds,
  userAds,
  categoryProperties,
  h1,
}: {
  children: ReactNode;
  adDetails?: AdDetails | null;
  premiumAds?: GetAdsResponse | null;
  popularRequests?: GetPopularRequestsResponse | null;
  regions?: GetRegionsResponse | null;
  rootCategories?: GetRootCategoriesResponse | null;
  currentCategory?: CategoryBaseInfo | null;
  vipAds?: GetAdsResponse | null;
  latestAds?: GetAdsResponse | null;
  userAds?: GetAdsResponse | null;
  categoryProperties?: GetCategoryPropertiesResponse | null;
  h1?: string | null;
}) => {
  const [resettableData, setResettableData] = useState({
    regions: regions ?? null,
    initialPremiumAds: premiumAds ?? null,
    popularRequests: popularRequests ?? null,
    rootCategories: rootCategories ?? null,
    currentCategory: currentCategory ?? null,
    initialVipAds: vipAds ?? null,
    initialLatestAds: latestAds ?? null,
    initialUserAds: userAds ?? null,
    categoryProperties: categoryProperties ?? null,
  });

  // Reset all unused data on the client side
  useEffect(() => {
    setResettableData({
      regions: null,
      initialPremiumAds: null,
      popularRequests: null,
      rootCategories: null,
      currentCategory: null,
      initialVipAds: null,
      initialLatestAds: null,
      initialUserAds: null,
      categoryProperties: null,
    });
  }, []);

  return (
    <ServerDataContext.Provider
      value={{
        initialAdDetails: adDetails ?? null,
        h1: h1 ?? null,
        ...resettableData,
      }}
    >
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerDataContext = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error(
      'useServerDataContext must be used within ServerDataProvider'
    );
  }
  return context;
};
